import React, { useEffect, useMemo } from 'react';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  InfoCircleOutlined,
  MinusOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import type { ApexOptions } from 'apexcharts';
import { Alert, Button, Spin, Tag, Tooltip, Typography } from 'antd';
import Chart from 'react-apexcharts';

import { inspectAssessmentPeriodPriceKey } from '../../../../state/app/inspect_assessment/actions';
import type {
  InspectAssessmentOptimalPortfolio,
  InspectAssessmentPeriod,
  InspectAssessmentPeriodPrices,
  InspectAssessmentState,
} from '../../../../state/app/inspect_assessment/types';
import {
  assessmentPeriodMarketChanges,
  finiteOrNull,
  formatPrice,
  pmChartPrice,
  relevantPeriodPriceSeries,
} from './chartData';
import { formatAssessmentDate } from './formatters';
import { describeIcs } from './ics';
import InstrumentSidecar from './InstrumentSidecar';
import PeriodChangeTag from './PeriodChangeTag';

const PERIOD_WIDTH = 280;
const AXIS_GUTTER = 72;
const PERIOD_EDGE_EPSILON = 0.000001;

type TimelineRange = {
  start: number;
  end: number;
};

type TimelinePoint = {
  x: number;
  y: number | null;
};

type TimelineSeries = {
  name: string;
  type: 'line';
  data: TimelinePoint[];
};

type PeriodEntry = {
  ordinal: number;
  period: InspectAssessmentPeriod;
  rawPcs: number | null;
  target: number | null;
  prices?: InspectAssessmentPeriodPrices;
  priceError?: string;
  priceLoading: boolean;
};

type PriceBucket = {
  assetId: number;
  assetName: string;
  pointsByPeriod: Map<number, TimelinePoint[]>;
};

type AxisDomain = {
  available: boolean;
  min: number;
  max: number;
  ticks: number[];
};

type RawPcsContributor = {
  headline: string;
  ics: ReturnType<typeof describeIcs>;
  newsId: number;
};

type Props = {
  state: InspectAssessmentState;
  portfolio?: InspectAssessmentOptimalPortfolio;
  fetched: boolean;
  fetching: boolean;
  error?: string;
  onLoad: () => void;
  onSelectInstrument: (instrumentId: number) => void;
};

const parseDate = (value?: string) => {
  if (!value) return undefined;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : undefined;
};

const periodRanges = (entries: PeriodEntry[]): Array<TimelineRange | null> =>
  entries.map((entry, ordinal) => {
    const start = parseDate(
      entry.prices?.start_date ?? entry.period.scenario_date,
    );
    const end = parseDate(
      entry.prices?.end_date ??
        entries[ordinal + 1]?.period.scenario_date ??
        entry.period.assessment_period?.end_date,
    );
    if (start === undefined || end === undefined || end <= start) return null;
    return { start, end };
  });

const timelineCoordinate = (
  timestamp: string,
  ordinal: number,
  range: TimelineRange | null,
) => {
  if (!range) return undefined;
  const parsed = parseDate(timestamp);
  if (parsed === undefined) return undefined;
  const progress = Math.min(
    1,
    Math.max(0, (parsed - range.start) / (range.end - range.start)),
  );
  return ordinal + progress;
};

const formatTimelineCoordinate = (
  coordinate: number,
  ranges: Array<TimelineRange | null>,
) => {
  if (!Number.isFinite(coordinate) || ranges.length === 0) return '';
  const ordinal = Math.min(
    ranges.length - 1,
    Math.max(0, Math.floor(coordinate)),
  );
  const range = ranges[ordinal];
  if (!range) return '';
  const progress =
    coordinate >= ranges.length
      ? 1
      : Math.min(1, Math.max(0, coordinate - ordinal));
  const timestamp = range.start + (range.end - range.start) * progress;
  return formatAssessmentDate(new Date(timestamp).toISOString());
};

const flattenPriceBucket = (bucket: PriceBucket) => {
  const periodPoints = Array.from(bucket.pointsByPeriod.entries()).sort(
    ([left], [right]) => left - right,
  );
  const points: TimelinePoint[] = [];
  let previousOrdinal: number | undefined;
  periodPoints.forEach(([ordinal, periodValues]) => {
    if (previousOrdinal !== undefined && ordinal > previousOrdinal + 1) {
      points.push(
        { x: previousOrdinal + 1 - PERIOD_EDGE_EPSILON, y: null },
        { x: ordinal + PERIOD_EDGE_EPSILON, y: null },
      );
    }
    points.push(...periodValues);
    previousOrdinal = ordinal;
  });
  return points;
};

const buildPriceSeries = (
  entries: PeriodEntry[],
  ranges: Array<TimelineRange | null>,
): TimelineSeries[] => {
  const buckets = new Map<number, PriceBucket>();
  entries.forEach(entry => {
    if (entry.prices?.status !== 'available') return;
    relevantPeriodPriceSeries(entry.prices).forEach(asset => {
      const points = asset.points
        .map(point => {
          const x = timelineCoordinate(
            point.timestamp,
            entry.ordinal,
            ranges[entry.ordinal],
          );
          const y = pmChartPrice(point, asset.is_fx_forward);
          return x === undefined || y === null ? undefined : { x, y };
        })
        .filter((point): point is TimelinePoint => point !== undefined);
      if (points.length === 0) return;
      const bucket = buckets.get(asset.underlying_asset_id) ?? {
        assetId: asset.underlying_asset_id,
        assetName:
          asset.ticker || asset.name || `Asset #${asset.underlying_asset_id}`,
        pointsByPeriod: new Map<number, TimelinePoint[]>(),
      };
      bucket.pointsByPeriod.set(entry.ordinal, points);
      buckets.set(asset.underlying_asset_id, bucket);
    });
  });

  const multipleAssets = buckets.size > 1;
  return Array.from(buckets.values())
    .sort((left, right) => left.assetId - right.assetId)
    .map(bucket => ({
      name: `${bucket.assetName}${
        multipleAssets ? ` #${bucket.assetId}` : ''
      } price`,
      type: 'line' as const,
      data: flattenPriceBucket(bucket),
    }));
};

const axisDomain = (values: number[], includeZero: boolean): AxisDomain => {
  const finiteValues = values.filter(Number.isFinite);
  if (finiteValues.length === 0) {
    return { available: false, min: 0, max: 1, ticks: [] };
  }
  let low = Math.min(...finiteValues);
  let high = Math.max(...finiteValues);
  if (includeZero) {
    low = Math.min(low, 0);
    high = Math.max(high, 0);
  }
  const span = high - low;
  const padding = span === 0 ? Math.max(Math.abs(high) * 0.08, 1) : span * 0.08;
  const min = low - padding;
  const max = high + padding;
  const ticks = Array.from(
    { length: 5 },
    (_, index) => max - ((max - min) * index) / 4,
  );
  return { available: true, min, max, ticks };
};

const formatExposure = (value: number) =>
  value.toLocaleString('en', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  });

const formatSignedExposure = (value?: number | null) =>
  value === undefined || value === null
    ? '—'
    : `${value > 0 ? '+' : ''}${formatExposure(value)}`;

const signedTagColor = (value?: number | null) =>
  value !== undefined && value !== null && value > 0
    ? 'green'
    : value !== undefined && value !== null && value < 0
      ? 'red'
      : undefined;

const RawPcsTag: React.FC<{
  contributors?: RawPcsContributor[];
  periodLabel: string;
  value: number | null;
}> = ({ contributors, periodLabel, value }) => {
  const tone =
    value !== null && value > 0
      ? 'positive'
      : value !== null && value < 0
        ? 'negative'
        : 'neutral';
  const display =
    value === null ? '—' : `${value > 0 ? '+' : ''}${value.toFixed(2)}`;

  return (
    <Tag
      className="assessment-inspect-assessment__optimal-raw-pcs-tag"
      variant="filled"
    >
      EXP. derived from news impact raw_pcs{' '}
      <span
        className={`assessment-inspect-assessment__optimal-raw-pcs-value assessment-inspect-assessment__optimal-raw-pcs-value--${tone}`}
      >
        {display}
      </span>
      <Tooltip
        styles={{ root: { maxWidth: 420 } }}
        title={
          <div className="assessment-inspect-assessment__optimal-raw-pcs-tooltip">
            <span className="assessment-inspect-assessment__optimal-raw-pcs-tooltip-title">
              News items contributing to raw_pcs
            </span>
            {contributors === undefined ? (
              <span>News contribution details are not loaded.</span>
            ) : contributors.length === 0 ? (
              <span>No news item has a non-zero ICS contribution.</span>
            ) : (
              contributors.map(contributor => (
                <span
                  className="assessment-inspect-assessment__optimal-raw-pcs-contributor"
                  key={contributor.newsId}
                >
                  <span>{contributor.headline}</span>
                  <span
                    className={`assessment-inspect-assessment__news-ics assessment-inspect-assessment__news-ics--${contributor.ics.tone}`}
                  >
                    {contributor.ics.text}
                  </span>
                </span>
              ))
            )}
          </div>
        }
      >
        <span
          aria-label={`${periodLabel} raw PCS news contributors`}
          className="assessment-inspect-assessment__optimal-raw-pcs-help"
          role="img"
          tabIndex={0}
        >
          <QuestionCircleOutlined aria-hidden="true" />
        </span>
      </Tooltip>
    </Tag>
  );
};

const periodPnlFrom = (exposure: number | null, priceChange?: number) => {
  if (exposure === null || priceChange === undefined) return undefined;
  const value = (exposure * priceChange) / 100;
  if (!Number.isFinite(value)) return undefined;
  return Object.is(value, -0) ? 0 : value;
};

const sourceLabel = (source: InspectAssessmentOptimalPortfolio['source']) => {
  switch (source) {
    case 'session_specific':
      return { color: 'blue', text: 'Session-specific optimal portfolio' };
    case 'shared_assessment':
      return { color: undefined, text: 'Shared assessment fallback' };
    case 'session_specific_with_shared_fallbacks':
      return {
        color: 'gold',
        text: 'Session-specific with shared assessment fallbacks',
      };
    default:
      return { color: undefined, text: 'No saved optimal portfolio' };
  }
};

const OPTIMAL_SOURCE_HELP =
  'Session-specific: optimal exposures saved for the selected session. ' +
  "Shared assessment fallback: the assessment's shared optimal exposures, used where the session has no saved exposure. " +
  'A mixed tag means both sources are used.';

const ExposureChangeTag: React.FC<{
  unit: InspectAssessmentOptimalPortfolio['instruments'][number]['unit'];
  value?: number;
}> = ({ unit, value }) => {
  const direction =
    value === undefined
      ? 'missing'
      : value > 0
        ? 'up'
        : value < 0
          ? 'down'
          : 'flat';

  return (
    <Tag
      className="assessment-inspect-assessment__optimal-delta-tag"
      color={signedTagColor(value)}
      variant="filled"
    >
      {direction === 'up' ? (
        <ArrowUpOutlined />
      ) : direction === 'down' ? (
        <ArrowDownOutlined />
      ) : direction === 'flat' ? (
        <MinusOutlined />
      ) : null}{' '}
      Δ EXP.{' '}
      {value === undefined ? '—' : `${formatSignedExposure(value)} ${unit}`}
    </Tag>
  );
};

const TurnoverTag: React.FC<{
  unit: InspectAssessmentOptimalPortfolio['instruments'][number]['unit'];
  value?: number;
}> = ({ unit, value }) => (
  <Tooltip title="Running total of absolute exposure changes. A decrease of 20 adds 20 to turnover.">
    <Tag
      className="assessment-inspect-assessment__optimal-turnover-tag"
      variant="filled"
    >
      TURNOVER {value === undefined ? '—' : formatExposure(value)} {unit}
    </Tag>
  </Tooltip>
);

const ReferencePnlTag: React.FC<{
  cumulative?: boolean;
  value?: number;
}> = ({ cumulative = false, value }) => {
  const direction =
    value === undefined
      ? 'missing'
      : value > 0
        ? 'up'
        : value < 0
          ? 'down'
          : 'flat';

  return (
    <Tooltip
      title={
        cumulative
          ? 'Admin reference only: the sum of period PnL from the first period through this period.'
          : 'Admin reference only: optimal exposure multiplied by the period price change.'
      }
    >
      <Tag
        className="assessment-inspect-assessment__optimal-pnl-tag"
        color={cumulative ? undefined : signedTagColor(value)}
        variant="filled"
      >
        {!cumulative &&
          (direction === 'up' ? (
            <ArrowUpOutlined />
          ) : direction === 'down' ? (
            <ArrowDownOutlined />
          ) : direction === 'flat' ? (
            <MinusOutlined />
          ) : null)}{' '}
        {cumulative ? 'PnL' : 'period PnL'}{' '}
        {value === undefined ? '—' : formatSignedExposure(value)} $M
      </Tag>
    </Tooltip>
  );
};

const FixedAxis: React.FC<{
  domain: AxisDomain;
  formatter: (value: number) => string;
  side: 'left' | 'right';
  title: string;
}> = ({ domain, formatter, side, title }) => (
  <div
    aria-label={`${title} y-axis`}
    className={`assessment-inspect-assessment__optimal-axis assessment-inspect-assessment__optimal-axis--${side}`}
    role="group"
  >
    <span className="assessment-inspect-assessment__optimal-axis-title">
      {title}
    </span>
    <div className="assessment-inspect-assessment__optimal-axis-ticks">
      {(domain.available ? domain.ticks : Array(5).fill(undefined)).map(
        (tick, index) => (
          <span key={index}>
            {typeof tick === 'number' ? formatter(tick) : '—'}
          </span>
        ),
      )}
    </div>
  </div>
);

const OptimalPortfolioWorkspace: React.FC<Props> = ({
  state,
  portfolio,
  fetched,
  fetching,
  error,
  onLoad,
  onSelectInstrument,
}) => {
  useEffect(() => {
    if (
      state.selectedAssessmentId !== undefined &&
      !fetched &&
      !fetching &&
      !error
    ) {
      onLoad();
    }
  }, [error, fetched, fetching, onLoad, state.selectedAssessmentId]);

  const instrument = state.instruments.find(
    item => item.id === state.selectedInstrumentId,
  );
  const optimalInstrument = portfolio?.instruments.find(
    item => item.instrument_id === instrument?.id,
  );
  const targetByPeriodIndex = useMemo(
    () =>
      new Map(
        (optimalInstrument?.periods ?? []).map(period => [
          period.period_index,
          finiteOrNull(period.target),
        ]),
      ),
    [optimalInstrument],
  );
  const rawPcsByPeriodIndex = useMemo(
    () =>
      new Map(
        (optimalInstrument?.periods ?? []).map(period => [
          period.period_index,
          finiteOrNull(period.raw_pcs),
        ]),
      ),
    [optimalInstrument],
  );
  const rawPcsContributorsByPeriodIndex = useMemo(() => {
    const contributorsByPeriod = new Map<
      number,
      RawPcsContributor[] | undefined
    >();
    state.periods.forEach(period => {
      const periodNews = state.periodNewsByIndex[period.index];
      if (!periodNews) {
        contributorsByPeriod.set(period.index, undefined);
        return;
      }
      contributorsByPeriod.set(
        period.index,
        periodNews.items
          .map(news => ({
            headline: news.headline,
            ics: describeIcs(news, instrument),
            newsId: news.id,
          }))
          .filter(
            contributor =>
              contributor.ics.value !== undefined &&
              Math.abs(contributor.ics.value) > 1e-12,
          ),
      );
    });
    return contributorsByPeriod;
  }, [instrument, state.periodNewsByIndex, state.periods]);
  const entries = useMemo<PeriodEntry[]>(
    () =>
      state.periods.map((period, ordinal) => {
        const priceKey =
          state.selectedAssessmentId !== undefined && instrument
            ? inspectAssessmentPeriodPriceKey(
                state.selectedAssessmentId,
                period.index,
                instrument.id,
              )
            : undefined;
        return {
          ordinal,
          period,
          rawPcs: rawPcsByPeriodIndex.get(period.index) ?? null,
          target: targetByPeriodIndex.get(period.index) ?? null,
          prices: priceKey ? state.periodPricesByKey[priceKey] : undefined,
          priceError: priceKey
            ? state.periodPricesErrorByKey[priceKey]
            : undefined,
          priceLoading: Boolean(
            priceKey &&
              (state.periodPricesFetchingByKey[priceKey] ||
                state.periodPricesBatchFetchingByKey[priceKey]),
          ),
        };
      }),
    [
      instrument,
      state.periodPricesBatchFetchingByKey,
      state.periodPricesByKey,
      state.periodPricesErrorByKey,
      state.periodPricesFetchingByKey,
      state.periods,
      state.selectedAssessmentId,
      rawPcsByPeriodIndex,
      targetByPeriodIndex,
    ],
  );
  const periodMetrics = useMemo(() => {
    let accumulatedPnl = 0;
    let accumulatedTurnover = 0;
    let pnlComplete = true;
    let turnoverComplete = true;

    return entries.map((entry, ordinal) => {
      const previousTarget = entries[ordinal - 1]?.target;
      const exposureChange =
        entry.target !== null &&
        previousTarget !== undefined &&
        previousTarget !== null
          ? entry.target - previousTarget
          : undefined;
      if (ordinal === 0) {
        turnoverComplete = entry.target !== null;
      } else if (exposureChange === undefined) {
        turnoverComplete = false;
      } else if (turnoverComplete) {
        accumulatedTurnover += Math.abs(exposureChange);
      }

      const priceChange = assessmentPeriodMarketChanges(
        entry.prices,
      ).pricePercentage;
      const periodPnl = periodPnlFrom(entry.target, priceChange);
      if (periodPnl === undefined) {
        pnlComplete = false;
      } else if (pnlComplete) {
        accumulatedPnl += periodPnl;
        if (Object.is(accumulatedPnl, -0)) accumulatedPnl = 0;
      }
      return {
        accumulatedPnl: pnlComplete ? accumulatedPnl : undefined,
        exposureChange,
        periodPnl,
        priceChange,
        turnover: turnoverComplete ? accumulatedTurnover : undefined,
      };
    });
  }, [entries]);
  const ranges = useMemo(() => periodRanges(entries), [entries]);
  const priceSeries = useMemo(
    () => buildPriceSeries(entries, ranges),
    [entries, ranges],
  );
  const exposureData = useMemo(
    () =>
      entries.flatMap(entry => [
        {
          x: entry.ordinal + PERIOD_EDGE_EPSILON,
          y: entry.target,
        },
        {
          x: entry.ordinal + 1 - PERIOD_EDGE_EPSILON,
          y: entry.target,
        },
      ]),
    [entries],
  );
  const exposureValues = useMemo(
    () =>
      entries
        .map(entry => entry.target)
        .filter((value): value is number => value !== null),
    [entries],
  );
  const priceValues = useMemo(
    () =>
      priceSeries.flatMap(series =>
        series.data
          .map(point => point.y)
          .filter((value): value is number => value !== null),
      ),
    [priceSeries],
  );
  const exposureDomain = useMemo(
    () => axisDomain(exposureValues, true),
    [exposureValues],
  );
  const priceDomain = useMemo(
    () => axisDomain(priceValues, false),
    [priceValues],
  );
  const hasExposure = exposureValues.length > 0;
  const series = useMemo<TimelineSeries[]>(
    () =>
      hasExposure
        ? [
            {
              name: 'Optimal exposure',
              type: 'line',
              data: exposureData,
            },
            ...priceSeries,
          ]
        : priceSeries,
    [exposureData, hasExposure, priceSeries],
  );
  const priceSeriesNames = useMemo(
    () => priceSeries.map(item => item.name),
    [priceSeries],
  );
  const options = useMemo<ApexOptions>(
    () => ({
      chart: {
        animations: { enabled: false },
        parentHeightOffset: 0,
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      colors: [
        ...(hasExposure ? ['#0f5fd6'] : []),
        ...priceSeries.map(() => '#d46b08'),
      ],
      dataLabels: { enabled: false },
      grid: {
        padding: {
          bottom: 0,
          left: AXIS_GUTTER,
          right: AXIS_GUTTER,
          top: 8,
        },
      },
      legend: { show: false },
      markers: { size: 0 },
      stroke: {
        curve: 'straight',
        width: [...(hasExposure ? [3] : []), ...priceSeries.map(() => 2)],
      },
      tooltip: {
        hideEmptySeries: true,
        shared: true,
        x: {
          formatter: value => formatTimelineCoordinate(Number(value), ranges),
        },
        y: [
          ...(hasExposure
            ? [
                {
                  formatter: (value: number) =>
                    `${formatExposure(value)} ${
                      optimalInstrument?.unit ?? ''
                    }`.trim(),
                },
              ]
            : []),
          ...priceSeries.map(() => ({ formatter: formatPrice })),
        ],
      },
      xaxis: {
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: { show: false },
        max: entries.length,
        min: 0,
        tickAmount: entries.length,
        tooltip: { enabled: false },
        type: 'numeric',
      },
      // ApexCharts crashes when a configured axis points at no series.
      // Price data arrives after exposure data, so omit an axis until its
      // corresponding series exists.
      yaxis: [
        ...(hasExposure
          ? [
              {
                axisBorder: { show: false },
                axisTicks: { show: false },
                labels: { show: false },
                max: exposureDomain.max,
                min: exposureDomain.min,
                seriesName: 'Optimal exposure',
                tickAmount: 4,
              },
            ]
          : []),
        ...(priceSeries.length > 0
          ? [
              {
                axisBorder: { show: false },
                axisTicks: { show: false },
                labels: { show: false },
                max: priceDomain.max,
                min: priceDomain.min,
                opposite: true,
                seriesName: priceSeriesNames,
                tickAmount: 4,
              },
            ]
          : []),
      ],
      annotations: hasExposure
        ? {
            yaxis: [
              {
                borderColor: '#8c9aae',
                strokeDashArray: 3,
                y: 0,
              },
            ],
          }
        : undefined,
    }),
    [
      entries.length,
      exposureDomain,
      hasExposure,
      optimalInstrument?.unit,
      priceDomain,
      priceSeries,
      priceSeriesNames,
      ranges,
    ],
  );
  const allPricesSettled = entries.every(
    entry => entry.prices || entry.priceError,
  );
  const priceBatchError = allPricesSettled ? undefined : state.periodBatchError;
  const dataStillLoading =
    entries.length > 0 && !allPricesSettled && !priceBatchError;
  const timelineWidth = entries.length * PERIOD_WIDTH + AXIS_GUTTER * 2;
  const alignedGridStyle = {
    gridTemplateColumns: `repeat(${entries.length}, ${PERIOD_WIDTH}px)`,
    paddingInline: AXIS_GUTTER,
  };
  // react-apexcharts reuses Apex's mutable series-to-axis map. Remount only
  // when that structure changes; ordinary point updates keep the same chart.
  const chartStructureKey = JSON.stringify([
    state.selectedAssessmentId,
    portfolio?.link_id ?? 'shared',
    instrument?.id,
    entries.length,
    series.map(item => item.name),
  ]);
  const source = portfolio ? sourceLabel(portfolio.source) : undefined;

  let body: React.ReactNode;
  if (state.selectedAssessmentId === undefined) {
    body = (
      <div className="assessment-inspect-assessment__full-empty">
        <Typography.Text type="secondary">
          Select an assessment to see its optimal portfolio.
        </Typography.Text>
      </div>
    );
  } else if (
    state.periodsFetching ||
    state.instrumentsFetching ||
    fetching ||
    !fetched
  ) {
    body = (
      <div className="assessment-inspect-assessment__full-empty">
        <Spin size="small" />
      </div>
    );
  } else if (error || state.periodsError || state.instrumentsError) {
    body = (
      <div className="assessment-inspect-assessment__full-empty">
        <Alert
          action={
            error ? (
              <Button onClick={onLoad} size="small">
                Retry
              </Button>
            ) : undefined
          }
          description={error || state.periodsError || state.instrumentsError}
          message="Unable to build the optimal portfolio timeline"
          showIcon
          type="error"
        />
      </div>
    );
  } else if (!instrument) {
    body = (
      <div className="assessment-inspect-assessment__full-empty">
        <Typography.Text type="secondary">
          Select an assessed instrument to see its optimal exposure.
        </Typography.Text>
      </div>
    );
  } else if (!optimalInstrument) {
    body = (
      <div className="assessment-inspect-assessment__full-empty">
        <Typography.Text type="secondary">
          No saved optimal-portfolio row is available for {instrument.code}.
        </Typography.Text>
      </div>
    );
  } else if (entries.length === 0) {
    body = (
      <div className="assessment-inspect-assessment__full-empty">
        <Typography.Text type="secondary">
          No assessment periods are available.
        </Typography.Text>
      </div>
    );
  } else {
    body = (
      <div className="assessment-inspect-assessment__optimal-main">
        <div className="assessment-inspect-assessment__full-heading">
          <div className="assessment-inspect-assessment__full-heading-copy">
            <Typography.Text strong>
              {instrument.code} optimal exposure and price
            </Typography.Text>
            <div className="assessment-inspect-assessment__optimal-legend">
              <span>
                <i className="assessment-inspect-assessment__optimal-legend-swatch assessment-inspect-assessment__optimal-legend-swatch--exposure" />
                Optimal exposure ({optimalInstrument.unit})
              </span>
              <span>
                <i className="assessment-inspect-assessment__optimal-legend-swatch assessment-inspect-assessment__optimal-legend-swatch--price" />
                Price
              </span>
            </div>
          </div>
          <div className="assessment-inspect-assessment__optimal-heading-status">
            {source && (
              <Tag
                className="assessment-inspect-assessment__optimal-source-tag"
                color={source.color}
                variant="filled"
              >
                {source.text}
                <Tooltip title={OPTIMAL_SOURCE_HELP}>
                  <QuestionCircleOutlined
                    aria-label="Explain optimal portfolio source"
                    className="assessment-inspect-assessment__optimal-source-help"
                  />
                </Tooltip>
              </Tag>
            )}
            {dataStillLoading && <Spin size="small" />}
          </div>
        </div>
        {priceBatchError && (
          <Alert
            banner
            description={priceBatchError}
            message="Some price data could not be loaded"
            showIcon
            type="warning"
          />
        )}
        <div className="assessment-inspect-assessment__optimal-timeline-shell">
          <div
            aria-label={`${instrument.code} optimal exposure and price timeline; scroll horizontally to inspect every period`}
            className="assessment-inspect-assessment__full-scroll"
            tabIndex={0}
          >
            <div
              className="assessment-inspect-assessment__optimal-timeline"
              style={{ width: timelineWidth }}
            >
              <div
                className="assessment-inspect-assessment__full-period-track"
                style={alignedGridStyle}
              >
                {entries.map(entry => {
                  const priceIssue =
                    entry.priceError ||
                    (entry.prices && entry.prices.status !== 'available'
                      ? entry.prices.message
                      : undefined);
                  return (
                    <div
                      className="assessment-inspect-assessment__full-period"
                      key={entry.period.index}
                    >
                      <div>
                        <Typography.Text strong>
                          {entry.period.label}
                        </Typography.Text>
                        <Typography.Text type="secondary">
                          {formatAssessmentDate(entry.period.scenario_date)}
                        </Typography.Text>
                      </div>
                      {entry.priceLoading && <Spin size="small" />}
                      {priceIssue && (
                        <Tooltip title={priceIssue}>
                          <span
                            aria-label={`${entry.period.label} price data information`}
                            className="assessment-inspect-assessment__full-period-info"
                            role="img"
                            tabIndex={0}
                          >
                            <InfoCircleOutlined />
                          </span>
                        </Tooltip>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="assessment-inspect-assessment__full-chart assessment-inspect-assessment__optimal-chart">
                {series.length > 0 ? (
                  <Chart
                    height="100%"
                    key={chartStructureKey}
                    options={options}
                    series={series}
                    type="line"
                  />
                ) : (
                  <div className="assessment-inspect-assessment__full-chart-empty">
                    {allPricesSettled ? (
                      <Typography.Text type="secondary">
                        No saved exposure or chartable price is available.
                      </Typography.Text>
                    ) : (
                      <>
                        <Spin size="small" />
                        <Typography.Text type="secondary">
                          Loading period prices…
                        </Typography.Text>
                      </>
                    )}
                  </div>
                )}
                <div
                  aria-hidden="true"
                  className="assessment-inspect-assessment__full-period-guides"
                  style={{
                    gridTemplateColumns: `repeat(${entries.length}, ${PERIOD_WIDTH}px)`,
                    left: AXIS_GUTTER,
                    right: AXIS_GUTTER,
                  }}
                >
                  {entries.map(entry => (
                    <span key={entry.period.index} />
                  ))}
                </div>
              </div>
              <div
                className="assessment-inspect-assessment__optimal-metrics-track"
                style={alignedGridStyle}
              >
                {entries.map((entry, ordinal) => {
                  const {
                    accumulatedPnl,
                    exposureChange,
                    periodPnl,
                    priceChange,
                    turnover,
                  } = periodMetrics[ordinal];
                  return (
                    <section
                      aria-label={`${entry.period.label} optimal exposure, changes, and reference PnL`}
                      className="assessment-inspect-assessment__optimal-period-metrics"
                      key={entry.period.index}
                    >
                      <Tag
                        className="assessment-inspect-assessment__optimal-exposure-tag"
                        color={signedTagColor(entry.target)}
                        variant="filled"
                      >
                        EXPOSURE {formatSignedExposure(entry.target)}{' '}
                        {optimalInstrument.unit}
                      </Tag>
                      <RawPcsTag
                        contributors={rawPcsContributorsByPeriodIndex.get(
                          entry.period.index,
                        )}
                        periodLabel={entry.period.label}
                        value={entry.rawPcs}
                      />
                      <div
                        aria-label="Exposure details divider"
                        className="assessment-inspect-assessment__optimal-metrics-divider"
                        role="separator"
                      />
                      <ExposureChangeTag
                        unit={optimalInstrument.unit}
                        value={exposureChange}
                      />
                      <PeriodChangeTag
                        change={{
                          key: `price-${entry.period.index}`,
                          label: 'PRICE',
                          unit: '%',
                          value: priceChange,
                        }}
                      />
                      <ReferencePnlTag value={periodPnl} />
                      <ReferencePnlTag cumulative value={accumulatedPnl} />
                      <TurnoverTag
                        unit={optimalInstrument.unit}
                        value={turnover}
                      />
                    </section>
                  );
                })}
              </div>
            </div>
          </div>
          <FixedAxis
            domain={exposureDomain}
            formatter={formatExposure}
            side="left"
            title={`Exposure · ${optimalInstrument.unit}`}
          />
          <FixedAxis
            domain={priceDomain}
            formatter={formatPrice}
            side="right"
            title="Price"
          />
        </div>
      </div>
    );
  }

  return (
    <section
      aria-label="Optimal portfolio workspace"
      className="assessment-inspect-assessment__optimal-workspace"
    >
      <InstrumentSidecar
        error={state.instrumentsError}
        fetching={state.instrumentsFetching}
        instruments={state.instruments}
        onSelectInstrument={onSelectInstrument}
        selectedAssessmentId={state.selectedAssessmentId}
        selectedInstrumentId={state.selectedInstrumentId}
      />
      {body}
    </section>
  );
};

export default OptimalPortfolioWorkspace;
